import React from 'react';
import { Button, Input, Text, makeStyles, shorthands } from '@fluentui/react-components';
import { ArrowLeft24Regular, Dismiss24Regular, Search24Regular } from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import { AlwaysOnScrollBarStyles, customColors, customFonts } from '../../../../styles';
import useManageAgentsPage from '../hooks/useManageAgentsPage';
import ManageAgentTile from './ManageAgentTile';

const useStyles = makeStyles({
    root: {
        width: '100%',
        height: '90%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        ...shorthands.padding('72px', '12px', '20px', '12px'),
        ...shorthands.overflow('auto'),
        backgroundColor: customColors.protivitiOffWhite,
        ...AlwaysOnScrollBarStyles,
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        ...shorthands.gap('12px'),
        ...shorthands.margin('0', '0', '24px', '0'),
        width: '100%',
    },
    backButtonRow: {
        width: '85%',
        display: 'flex',
        justifyContent: 'flex-start',
        ...shorthands.margin('0px', '0px', '8px', '0px'),
    },
    backButton: {
        height: '28px',
        fontFamily: customFonts.Lato,
        fontSize: '15px',
        fontWeight: '700',
        textDecoration: 'underline',
        color: customColors.protivitiDarkBlue,
        ...shorthands.borderRadius('5px'),
        ...shorthands.padding('0px', '10px'),
        '&:hover': {
            color: customColors.summarizeButtonDarker,
        },
    },
    title: {
        fontFamily: customFonts.TitleFont,
        fontSize: '48px',
        color: customColors.summarizeButtonDarker,
        textAlign: 'center',
        marginBottom: '20px',
    },
    search: {
        width: '380px',
        maxWidth: '90vw',
    },
    clearButton: {
        minWidth: 'auto',
        padding: '0',
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: 'transparent',
        },
    },
    createButton: {
        ...shorthands.border('1px', 'solid', customColors.protivitiDarkBlue),
        color: customColors.protivitiDarkBlue,
        backgroundColor: customColors.white,
        ':hover': {
            backgroundColor: customColors.protivitiDarkBlue,
            color: customColors.white,
            ...shorthands.borderColor(customColors.protivitiOrange),
        },
    },
    list: {
        width: '85%',
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-evenly',
        ...shorthands.margin('8px', '0', '12px', '0'),
        ...shorthands.padding('16px', '0', '32px', '0'),
    },
    empty: {
        width: '85%',
        textAlign: 'center',
        ...shorthands.padding('36px', '0'),
        fontFamily: customFonts.Lato,
        color: customColors.darkNavy,
    },
});

const ManageAgentsPage: React.FC = () => {
    const styles = useStyles();
    const vm = useManageAgentsPage();
    const navigate = useNavigate();

    return (
        <div className={styles.root}>
            <div className={styles.backButtonRow}>
                <Button
                    type="button"
                    appearance="transparent"
                    className={styles.backButton}
                    icon={<ArrowLeft24Regular />}
                    onClick={() => navigate('/explore-agents')}
                >
                    Back to Explore Agents
                </Button>
            </div>
            <div className={styles.header}>
                <Text className={styles.title}>Manage Agents</Text>
                <Input
                    className={styles.search}
                    contentBefore={<Search24Regular />}
                    contentAfter={
                            vm.searchQuery && (
                                <Button
                                    appearance="transparent"
                                    className={styles.clearButton}
                                    icon={<Dismiss24Regular />}
                                    onClick={() => vm.setSearchQuery('')}
                                    aria-label="Clear search"
                                />
                            )
                        }
                    placeholder="Search agents"
                    value={vm.searchQuery}
                    onChange={(event) => vm.setSearchQuery(event.target.value)}
                />
                <Button className={styles.createButton} onClick={vm.onCreate}>
                    Create New Agent
                </Button>
            </div>

            {vm.isLoading && vm.items.length === 0 ? (
                <div className={styles.empty}>
                    <Text>Loading agents...</Text>
                </div>
            ) : vm.filteredItems.length === 0 ? (
                <div className={styles.empty}>
                    <Text>No agents found.</Text>
                </div>
            ) : (
                <div className={styles.list}>
                    {vm.filteredItems.map((item) => (
                        <ManageAgentTile
                            key={item.agent.agentId}
                            agent={item.agent}
                            avatarSrc={item.avatarSrc}
                            isSoftDeleted={item.isSoftDeleted}
                            onEdit={vm.onEdit}
                            onSoftDelete={vm.onSoftDelete}
                            onHardDelete={vm.onHardDelete}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default ManageAgentsPage;
